import { JURISDICTION_LIST } from '@/lib/constants';
import { TAgent, useUpdateAgentDetails } from '@/models/agents';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  MultiSelect,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

interface AgentDetailsFormProps {
  agent: TAgent;
  onSuccess?: (data: TAgent) => void;
  onCancel?: () => void;
}

export function AgentDetailsForm({
  agent,
  onSuccess,
  onCancel,
}: AgentDetailsFormProps) {
  const [loading, setLoading] = useState(false);
  const updateAgentDetails = useUpdateAgentDetails(agent.id);

  const form = useForm({
    initialValues: agent,
    validate: {
      acceptsFiat: (value, values) =>
        !value && !values.acceptsCrypto
          ? 'At least one payment method must be selected'
          : null,
      acceptsCrypto: (value, values) =>
        !value && !values.acceptsFiat
          ? 'At least one payment method must be selected'
          : null,
    },
  });

  useEffect(() => {
    form.setInitialValues(agent);
    form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  const handleSubmit = async (
    values: Pick<TAgent, 'jurisdictions' | 'acceptsFiat' | 'acceptsCrypto'>,
  ) => {
    setLoading(true);
    const { jurisdictions, acceptsFiat, acceptsCrypto } = values;
    updateAgentDetails.mutate(
      { jurisdictions, acceptsFiat, acceptsCrypto },
      {
        onSuccess: (updatedDetails) => {
          form.setValues({
            jurisdictions: updatedDetails.jurisdictions,
            acceptsFiat: updatedDetails.acceptsFiat,
            acceptsCrypto: updatedDetails.acceptsCrypto,
          });
          form.resetDirty();
          if (onSuccess) {
            onSuccess(updatedDetails);
          }
          notifications.show({
            title: 'Success',
            message: 'Agent details have been updated successfully',
            color: 'green',
            position: 'top-right',
          });
        },
        onSettled: () => {
          setLoading(false);
        },
      },
    );
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} key={agent.id}>
      <Flex direction="column" gap="md">
        {agent.updated_at && (
          <>
            <Box>
              <Text size="xs" fw={500} mb={5}>
                Effective Since
              </Text>
              <Text size="xs" c="dimmed">
                {dayjs(agent.updated_at).format('DD.MM.YYYY HH:mm')}
              </Text>
            </Box>
          </>
        )}

        <MultiSelect
          key={form.key('jurisdictions')}
          size="xs"
          label="Jurisdictions"
          description="Jurisdictions where this agent can operate. Used for filtering."
          data={JURISDICTION_LIST.map(({ code, title }) => ({
            value: code,
            label: title,
          }))}
          searchable
          clearable
          {...form.getInputProps('jurisdictions')}
        />

        <Box>
          <Text size="xs" fw={500} mb={5}>
            Payment Methods
          </Text>
          <Flex direction="column" gap="xs">
            <Checkbox
              label="Accepts Fiat"
              {...form.getInputProps('acceptsFiat', { type: 'checkbox' })}
            />
            <Checkbox
              label="Accepts Crypto"
              {...form.getInputProps('acceptsCrypto', { type: 'checkbox' })}
            />
          </Flex>
        </Box>

        <Group justify="flex-end" mt="md">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            loading={loading}
            disabled={!form.isDirty()}
            size="xs"
          >
            Save Changes
          </Button>
        </Group>
      </Flex>
    </form>
  );
}
